'use client';

import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { colorizeMarkup } from '@/components/ui/Cbullshitcoding';
import { ColorType } from '@/theme';
import type { Temper } from '@/types/frontend-types';
import {
  ContactFormStep,
  extractKeywords,
  getCodingTemper,
  messageByteSize,
  shouldClearCodingOnEnd,
} from './footerModels';

interface UseFooterCodingInput {
  contactStep: ContactFormStep;
  isMobile: boolean;
  committedEmail: string;
  committedMessage: string;
}

export interface FooterCodingController {
  codingStyle: CSSProperties;
  codingFadeWrapperStyle: CSSProperties;
  codingTemper: Temper;
  codingClearOnEnd: boolean;
  codeReplacements: Record<string, string>;
}

export function useFooterCoding({
  contactStep,
  isMobile,
  committedEmail,
  committedMessage,
}: UseFooterCodingInput): FooterCodingController {
  const codingStyle = useMemo(() => ({
    opacity: contactStep === ContactFormStep.ErrorStep ? 1 : 0.3,
    height: isMobile ? 220 : 350,
  }), [contactStep, isMobile]);

  const codingFadeWrapperStyle = useMemo(() => ({
    marginLeft: isMobile ? 110 : 200,
  }), [isMobile]);

  const codeReplacements = useMemo(() => {
    const mail = colorizeMarkup(committedEmail, ColorType.Warn);
    const request = committedMessage.replace(/\s+/g, ' ').trim();
    return {
      USER_NAME: mail,
      USER_EMAIL: mail,
      RANDOM_HEX: request,
      MESSAGE_SIZE_BYTES: String(messageByteSize({ text: committedMessage })),
      DETECTED_KEYWORDS: extractKeywords({ text: committedMessage }),
    };
  }, [committedEmail, committedMessage]);

  return {
    codingStyle,
    codingFadeWrapperStyle,
    codingTemper: getCodingTemper({ contactStep }),
    codingClearOnEnd: shouldClearCodingOnEnd({ contactStep }),
    codeReplacements,
  };
}
